import Vue from 'vue';
import Router, { Route } from 'vue-router';
import AuthorizationCallback from '@/views/AuthorizationCallback';
import SilentRefreshTokenCallback from '@/views/SilentRefreshTokenCallback';
import appStore from '@/store/app';
import authenticator from '@/store/authentication';

import Home from '@/views/Home';
import Users from '@/views/Users';
import User from '@/views/User';
import UserProfile from '@/views/UserProfile';
import Unauthorized from '@/views/Unauthorized';
import ResetPassword from '@/views/ResetPassword';
import Products from '@/views/Products';
import ProductDetails from '@/views/ProductDetails';
import Cart from '@/views/Cart';
import SystemAvailability from '@/views/SystemAvailability';
import OrdersTransmitted from '@/views/OrdersTransmitted';
import OrderTransmitted from '@/views/OrderTransmitted';
import ErpAmounts from '@/views/ErpAmounts';
import ErpStatements from '@/views/ErpStatements';
import ErpOrders from '@/views/ErpOrders';
import ErpOrderLines from '@/views/ErpOrderLines';
import ErpMarket from '@/views/ErpMarket';
import ErpMarketDetail from '@/views/ErpMarketDetail';
import ErpProductsInOrders from '@/views/ErpProductsInOrders';
import Banners from '@/views/Banners';
import Banner from '@/views/Banner';
import Partners from '@/views/Partners';

Vue.use(Router);

function transformIdInProps(route: Route): object {
  if (route.params) {
    return {
      id: route.params.id,
    };
  } else {
    return {
      id: '',
    };
  }
}

function transformIdInNumericProps(route: Route): object {
  if (route.params) {
    return {
      id: route.params.id ? parseInt(route.params.id, 0) : 0,
    };
  } else {
    return {
      id: 0,
    };
  }
}

function transformResetProps(route: Route): object {
  if (route.params) {
    return {
      id: route.params.id,
      token: route.params.token,
    };
  } else {
    return {
      id: '',
      token: '',
    };
  }
}

function transformProductProps(route: Route): object {
  if (route.params) {
    return {
      type: route.params.type,
      pharmacode: route.params.pharmacode,
      quantity: route.params.quantity ? parseInt(route.params.quantity, 0) : 1,
    };
  } else {
    return {
      type: '',
      pharmacode: '',
      quantity: 1,
    };
  }
}

function transformOrderLinesProps(route: Route): object {
  if (route.params) {
    return {
      day: route.params.day ? parseInt(route.params.day, 0) : 0,
      month: route.params.month ? parseInt(route.params.month, 0) : 0,
      year: route.params.year ? parseInt(route.params.year, 0) : 0,
      id: route.params.id ? parseInt(route.params.id, 0) : 1,
    };
  } else {
    return {
      day: 0,
      month: 0,
      year: 0,
      id: 0,
    };
  }
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
    },
    {
      path: '/statements',
      name: 'statements',
      component: ErpStatements,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/amounts',
      name: 'amounts',
      component: ErpAmounts,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/orders',
      name: 'orders',
      component: ErpOrders,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/productsInOrders',
      name: 'productsInOrders',
      component: ErpProductsInOrders,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/markets',
      name: 'markets',
      component: ErpMarket,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/market/:id',
      name: 'market',
      component: ErpMarketDetail,
      meta: {
        requiresAuthentication: true,
      },
      props: transformIdInNumericProps,
    },
    {
      path: '/ordersLines/:day/:month/:year/:id',
      name: 'ordersLines',
      component: ErpOrderLines,
      meta: {
        requiresAuthentication: true,
      },
      props: transformOrderLinesProps,
    },
    {
      path: '/users',
      name: 'users',
      component: Users,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/user/:id',
      name: 'user',
      component: User,
      meta: {
        requiresAuthentication: true,
      },
      props: transformIdInProps,
    },
    {
      path: '/banners',
      name: 'banners',
      component: Banners,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/banner/:id',
      name: 'banner',
      component: Banner,
      meta: {
        requiresAuthentication: true,
      },
      props: transformIdInProps,
    },
    {
      path: '/profile',
      name: 'profile',
      component: UserProfile,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/partners',
      name: 'partners',
      component: Partners,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/products',
      name: 'products',
      component: Products,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/cart',
      name: 'cart',
      component: Cart,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/transmitted',
      name: 'ordersTransmitted',
      component: OrdersTransmitted,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: '/order/:id',
      name: 'orderTransmitted',
      component: OrderTransmitted,
      meta: {
        requiresAuthentication: true,
      },
      props: transformIdInProps,
    },
    {
      path: '/product/:type/:pharmacode/:quantity?',
      name: 'product',
      component: ProductDetails,
      meta: {
        requiresAuthentication: true,
      },
      props: transformProductProps,
    },
    {
      path: '/auth-callback',
      name: 'authorizationCallback',
      component: AuthorizationCallback,
    },
    {
      path: '/silent-refresh',
      name: 'silentRefreshCallback',
      component: SilentRefreshTokenCallback,
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: Unauthorized,
    },
    {
      path: '/resetPassword/:id/:token',
      name: 'resetPassword',
      component: ResetPassword,
      props: transformResetProps,
    },
    {
      path: '/systemavailability',
      name: 'systemavailability',
      component: SystemAvailability,
    },
  ],
});

router.beforeEach(async (to: Route, from: Route, next) => {

  if (authenticator.isLoggedIn) {
    next();
  } else if (to.matched.some((record) => record.meta.requiresAuthentication)) {
    await authenticator.checkSignInAsync()
            .then(() => { next(); });
  } else {
    // Not authenticated. Try to fetch the user to see if it is logged-in.
    await authenticator.getUserAsync();
    next();
  }
});

router.afterEach(async (to, from) => {
  await appStore.checkSystemStatusAsync();
});

/* Collapse mobile aside menu on route change */
router.afterEach(() => {
  appStore.toggleMenuMobile(false);
});

export default router;
